import { NumberView, NumberViewProps } from '../number-view';
import { resolveBaseToken, sameAddress } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { ENV_BASE_TOKENS } from '@/libs/react-sdk/api/use-base-tokens';
import { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type CurrencyViewProps = HTMLAttributes<HTMLSpanElement> &
  NumberViewProps & {
    children?: ReactNode;
    showIcon?: boolean;
    showUnit?: boolean;
    baseTokenAddress?: string;
    unitClassName?: string;
    numberClassName?: string;
  };

export const CurrencyView = ({
  value,
  decimal,
  precision = 2,
  isChange = false,
  isLoading = false,
  animated = false,
  showSign = true,
  compact = true,
  baseTokenAddress,
  showIcon,
  showUnit,
  children,
  className,
  unitClassName,
  numberClassName,
  ...attrs
}: CurrencyViewProps) => {
  const { t } = useTranslation();
  const baseToken = useAppSettingStore(state => state.baseToken);
  // Token metadata comes from the module constant (same data useBaseTokens
  // serves), so no per-instance query observer and no derived-value memos —
  // these are trivial lookups over a tiny static list.
  const finalToken = resolveBaseToken(baseTokenAddress) || baseToken;
  const baseTokenInfo = ENV_BASE_TOKENS.baseTokens?.find(item => sameAddress(item.baseTokenAddress, finalToken.address));
  const finalDecimal = Number(decimal ?? baseTokenInfo?.baseTokenDecimals ?? finalToken.decimals);
  const finalPrecision = Number(precision);
  return (
    <span {...attrs} className={cn('inline-flex items-center gap-.5 flex-nowrap', className)}>
      {showIcon && finalToken.icon && <finalToken.icon className="size-[1em] flex-shrink-0" />}
      <NumberView
        value={value}
        decimal={finalDecimal}
        precision={finalPrecision}
        isChange={isChange}
        isLoading={isLoading}
        animated={animated}
        showSign={showSign}
        compact={compact}
        className={numberClassName}
      />
      {/* Unit hugs the number ("$1.2K DPS") so trailing labels like "Vol."
          passed as children don't wedge between value and currency */}
      {showUnit && <span className={cn(unitClassName)}>{t(finalToken.translateKey)}</span>}
      {children}
    </span>
  );
};
